import { Component, createSignal, Show, createEffect } from 'solid-js'
import { AppSchema } from '/common/types'
import { getAssetUrl } from '../../util'
import Fuse from 'fuse.js'

const GenerateImageCharacterModal: Component<{
  characters?: AppSchema.Character[]
  show: boolean
  hide: () => void
  onSelect?: (character: AppSchema.Character) => void
  selectedCharacter?: AppSchema.Character
}> = (props) => {
  const [selectedCharacter, setSelectedCharacter] = createSignal<AppSchema.Character | undefined>(
    undefined
  )
  const [searchQuery, setSearchQuery] = createSignal('')
  const [fuse, setFuse] = createSignal<Fuse<AppSchema.Character>>()

  // Initialize Fuse when characters are available
  createEffect(() => {
    if (props.characters) {
      setFuse(
        new Fuse(props.characters, {
          keys: ['name'],
          threshold: 0.3,
        })
      )
    }

    if (props.selectedCharacter) {
      setSelectedCharacter(props.selectedCharacter)
    }
  })

  // Filter characters based on search query
  const filteredCharacters = () => {
    const query = searchQuery()
    if (!query || !fuse()) return props.characters || []

    return fuse()!
      .search(query)
      .map((result) => result.item)
  }

  return (
    <Show when={props.show}>
      <div
        class="fixed bottom-0 left-0 right-0 top-0 z-[99] flex h-screen items-center justify-center overflow-y-auto overflow-x-hidden bg-black/80 backdrop-blur"
        onClick={(e) => {
          props.hide()
        }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation()
          }}
          class="background m-5 w-full rounded-[22px] border-[1px] border-white md:w-auto"
        >
          <div class="flex flex-col items-center justify-between gap-4 px-[18px] pb-4 pt-[18px] md:flex-row md:px-8 md:pt-8">
            <p class="mr-0 shrink-0 font-clash text-2xl font-semibold md:mr-24">Choose character</p>
            <input
              type="text"
              placeholder="Search characters..."
              value={searchQuery()}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
              class="w-full rounded-md bg-cosplay-purple/20 px-4 py-3 text-white placeholder:text-gray-400 focus:outline-none"
            />
          </div>

          <div class="grid h-[400px] grid-cols-2 gap-4 overflow-y-scroll px-[18px] pb-4 md:grid-cols-3">
            {filteredCharacters().map((c) => {
              const selected = c?._id === selectedCharacter()?._id

              return (
                <button
                  class={`relative ${
                    selected
                      ? 'rounded-xl border-[5px] border-cosplay-pink-100'
                      : 'border-[5px] border-transparent'
                  }`}
                  onClick={() => props.onSelect && setSelectedCharacter(c)}
                >
                  <div class="relative h-[180px] w-full overflow-hidden rounded-md md:h-[250px] md:w-[194px] md:shrink-0 md:grow-0">
                    {c?.avatar && (
                      <img
                        class="absolute h-full w-full object-cover"
                        src={getAssetUrl(c?.avatar)}
                      />
                    )}
                  </div>
                  <p class="absolute inset-x-0 bottom-0 rounded-b-md bg-gradient-to-t from-black to-transparent py-3 text-center text-sm font-semibold">
                    {c?.name}
                  </p>
                </button>
              )
            })}
          </div>
          <div class="flex flex-row justify-between gap-5 border-t border-t-cosplay-gray-100 p-[18px] pt-5 md:p-8 ">
            <button
              class="flex flex-1 justify-center rounded-md border border-cosplay-gray-100 p-2 font-semibold"
              onClick={(e) => {
                props.hide()
              }}
            >
              Cancel
            </button>
            <button
              class={`flex flex-1 justify-center rounded-md  p-2 font-semibold ${
                !selectedCharacter()
                  ? 'cursor-not-allowed bg-cosplay-purple/20'
                  : 'cursor-pointer bg-cosplay-purple'
              }`}
              disabled={!selectedCharacter()}
              onClick={() => {
                const char = selectedCharacter()
                if (props.onSelect && char) {
                  props.onSelect(char)
                }
              }}
            >
              Select
            </button>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default GenerateImageCharacterModal
